import React, { useCallback, useEffect, useState } from 'react'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession, selectRoomSessionIsLoading, setRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useMeetingUsers } from '../hooks/users'
import { ExternalUserServiceIntegration, Me, User } from '../../../../../models/User'
import { NavLink } from 'react-router-dom'
import buttonStyles from '../../../../../components/Button/Button.module.scss'
import { getUserDisplayName } from '../../common/aifeatures/utils'
import { UserTag } from '../../UserTag'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { Button } from '../../../../../components/Button/Button'
import { AddParticipantModal } from './AddParticipantModal'
import { useGetRoomSessionQuery, useUpdateSessionConfigMutation } from '../../../../../store/media/media'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { CONTACT_LINKING_SUPPORTED_INTEGRATIONS, getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import './Participants.scss'

import { useGetMeQuery } from '../../../../../store/user/user'
import { LinkContactModal } from './LinkContactModal'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { TrackEvent, useTracking } from '../../common/tracking'
import { ContactLanguageDisplay } from '../../MeetingSummary/MeetingInfo/Language'
import { LanguageConfigSource, MeetingLanguage, RoomSessionLanguageConfig } from '../../../../../models/Room'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { SummaryToast } from '../../MeetingSummary/Common/SummaryToast'
import { getLanguageLabel } from '../../../../../common/languageUtils'
import { useMultiLanguageEnabled } from '../../../../../common/useMultiLanguageEnabled'
import { ChangeMeetingLanguageModal, LanguageChangeReason } from '../../../../../views/LanguageSupport/ChangeMeetingLanguageModal'
import { RemoveParticipantModal } from './RemoveParticipantModal'

export interface ParticipantsProps {
  interactionsLoading: boolean
  onContactUpdated?: (serviceId: IntegrationServiceId) => void
}

const StyledIntegrationIcon = ({ serviceId }: { serviceId: IntegrationServiceId }) => {
  return (
    <>
      <IntegrationIcon serviceId={serviceId} fill='var(--zocks-gray-900)' width='1rem' height='1rem' />
      <i className='integration-icon-badge bi bi-circle-fill' />
    </>
  )
}

export const Participants: React.FC<ParticipantsProps> = ({ interactionsLoading, onContactUpdated }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const roomSession = useAppSelector(selectRoomSession)
  const { roomId = '', id: sessionId = '' } = roomSession ?? {}
  const roomSessionIsLoading = useAppSelector(selectRoomSessionIsLoading)
  const { usersByRoles, isFetching: isUsersFetching, refetchUsers: refetchContact } = useMeetingUsers(roomSession, ['external'])
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false)
  const contacts = usersByRoles['Clients'] ?? []
  const { data: currentUserData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [showLinkContactModal, setShowLinkContactModal] = useState<boolean>(false)
  const [serviceId, setServiceId] = useState<IntegrationServiceId | null>(null)
  const [user, setUser] = useState<User>()
  const { tracked } = useTracking()
  const roomSessionLanguageConfig = roomSession?.config?.language
  const [showRemoveParticipantModal, setShowRemoveParticipantModal] = useState(false)
  const [userToRemove, setUserToRemove] = useState<User | null>(null)
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const [oldLanguageConfig, setOldLanguageConfig] = useState<RoomSessionLanguageConfig | null>(null)
  const [updateRoomSessionConfig, { isLoading: isUpdatingSessionConfig }] = useUpdateSessionConfigMutation()
  const { hasQuota } = useZocksQuota()
  const hasMultiLanguageSupportQuota = hasQuota('ai.multiLanguageSupport.value')
  const [showLanguageChangedToast, setShowLanguageChangedToast] = useState(false)
  const [toastText, setToastText] = useState('')
  const [languageChangeReason, setLanguageChangeReason] = useState<LanguageChangeReason>('manual')
  const { multiLanguageEnabled } = useMultiLanguageEnabled()

  const [pollingState, setPollingState] = useState<{
    isActive: boolean
    reason: LanguageChangeReason | null
    startTime: number | null
    baselineLanguageConfig: RoomSessionLanguageConfig | null
    baselineCalculatedConfig: RoomSessionLanguageConfig | null
    modalShown: boolean
  }>({
    isActive: false,
    reason: null,
    startTime: null,
    baselineLanguageConfig: null,
    baselineCalculatedConfig: null,
    modalShown: false,
  })

  const shouldSkipPolling = !pollingState.isActive || !roomId || !sessionId
  const { data: polledRoomSessionData } = useGetRoomSessionQuery(
    { accountId, roomId, sessionId },
    {
      skip: shouldSkipPolling,
      pollingInterval: pollingState.isActive ? 1000 : 0,
    },
  )

  const showLanguageCalculatedModal = useCallback(
    (oldConfig: RoomSessionLanguageConfig | null, newLanguage: MeetingLanguage, reason: LanguageChangeReason) => {
      const _showModal = () => {
        setOldLanguageConfig(oldConfig ?? null)
        setLanguageChangeReason(reason)
        setShowLanguageModal(true)
      }

      tracked(_showModal, {
        eventName: TrackEvent.MEETING_LANGUAGE_CALCULATED,
        eventDetails: {
          accountId: accountId,
          roomId: roomId,
          sessionId: sessionId,
          newLanguage: newLanguage,
          oldLanguage: oldConfig?.language ?? '',
        },
      })()
    },
    [accountId, roomId, sessionId, tracked],
  )

  const stopPolling = useCallback((reason: LanguageChangeReason | null) => {
    setPollingState({
      isActive: false,
      reason: null,
      startTime: null,
      baselineLanguageConfig: null,
      baselineCalculatedConfig: null,
      modalShown: false,
    })

    if (reason === 'client_added') {
      setShowAddParticipantModal(false)
    } else if (reason === 'client_removed') {
      setShowRemoveParticipantModal(false)
      setUserToRemove(null)
    }
  }, [])

  useEffect(() => {
    if (!pollingState.isActive || !pollingState.startTime) {
      return
    }

    const MAX_POLL_TIME_MS = 3000
    const elapsedTime = Date.now() - pollingState.startTime
    const remainingTime = MAX_POLL_TIME_MS - elapsedTime

    if (remainingTime <= 0) {
      stopPolling(pollingState.reason)
      return
    }

    const timeoutId = setTimeout(() => {
      stopPolling(pollingState.reason)
    }, remainingTime)

    return () => clearTimeout(timeoutId)
  }, [pollingState.isActive, pollingState.startTime, pollingState.reason, stopPolling])

  useEffect(() => {
    if (!pollingState.isActive || !polledRoomSessionData?.data) {
      return
    }

    const currentCalculatedConfig = polledRoomSessionData.data.config?.calculatedLanguage
    const currentLanguageConfig = polledRoomSessionData.data.config?.language

    const baselineCalculatedUpdatedAt = pollingState.baselineCalculatedConfig?.updatedAt
    const currentCalculatedUpdatedAt = currentCalculatedConfig?.updatedAt

    const hasCalculatedLanguageChanged =
      currentCalculatedUpdatedAt && (!baselineCalculatedUpdatedAt || currentCalculatedUpdatedAt !== baselineCalculatedUpdatedAt)

    if (hasCalculatedLanguageChanged) {
      dispatch(setRoomSession(polledRoomSessionData.data))

      const currentSource = currentLanguageConfig?.source
      const baselineLanguage = pollingState.baselineLanguageConfig?.language
      const currentLanguage = currentLanguageConfig?.language

      if (
        !pollingState.modalShown &&
        currentSource !== LanguageConfigSource.MANUAL &&
        currentLanguage &&
        currentLanguage !== baselineLanguage &&
        pollingState.reason
      ) {
        showLanguageCalculatedModal(pollingState.baselineLanguageConfig, currentLanguage, pollingState.reason)
        setPollingState((prev) => ({ ...prev, modalShown: true }))
      }
      stopPolling(pollingState.reason)
      return
    }
  }, [pollingState, polledRoomSessionData, dispatch, showLanguageCalculatedModal, stopPolling])

  const handleAddParticipantClicked = () => {
    setShowAddParticipantModal(true)
  }

  const startPollingForLanguageChange = useCallback(
    (reason: LanguageChangeReason) => {
      if (!multiLanguageEnabled || !hasMultiLanguageSupportQuota) {
        return
      }

      const calculatedLanguageConfig = roomSession?.config?.calculatedLanguage
      setPollingState({
        isActive: true,
        reason,
        startTime: Date.now(),
        baselineLanguageConfig: roomSessionLanguageConfig ? { ...roomSessionLanguageConfig } : null,
        baselineCalculatedConfig: calculatedLanguageConfig ? { ...calculatedLanguageConfig } : null,
        modalShown: false,
      })
    },
    [multiLanguageEnabled, hasMultiLanguageSupportQuota, roomSession, roomSessionLanguageConfig],
  )

  const handleParticipantAdded = () => {
    startPollingForLanguageChange('client_added')
    if (!multiLanguageEnabled || !hasMultiLanguageSupportQuota) {
      setShowAddParticipantModal(false)
    }
  }

  const handleParticipantRemoved = () => {
    startPollingForLanguageChange('client_removed')
    if (!multiLanguageEnabled || !hasMultiLanguageSupportQuota) {
      setShowRemoveParticipantModal(false)
      setUserToRemove(null)
    }
  }

  const _handleSaveLinkContactModal = useCallback(() => {
    setShowLinkContactModal(false)
    refetchContact()
    if (serviceId) {
      onContactUpdated?.(serviceId)
    }
  }, [refetchContact, onContactUpdated, serviceId])

  const handleSaveLinkContactModal = tracked(_handleSaveLinkContactModal, {
    eventName: TrackEvent.CONTACT_LINKED_TO_INTEGRATION,
    eventDetails: { type: 'manual', source: 'meetingPreparation', serviceId: serviceId, userId: user?.id },
  })

  const handleCancelLinkContactModal = () => {
    setShowLinkContactModal(false)
  }

  const handleUpdateLanguage = async (language: RoomSessionLanguageConfig) => {
    if (!roomSession) {
      return
    }
    const result = await updateRoomSessionConfig({
      accountId,
      roomId: roomSession.roomId,
      sessionId: roomSession.id,
      body: {
        language: language,
      },
    })
    if ('data' in result && result.data?.data) {
      dispatch(setRoomSession(result.data.data))
    }
  }

  const handleLanguageAcknowledge = (language: MeetingLanguage, reason: LanguageChangeReason) => {
    const languageLabel = getLanguageLabel(language)
    if (reason === 'client_added') {
      setToastText(`New client added successfully. Meeting language set to ${languageLabel}.`)
    } else if (reason === 'client_removed') {
      setToastText(`Client removed successfully. Meeting language set to ${languageLabel}.`)
    }
    if (reason === 'client_added' || reason === 'client_removed') {
      setShowLanguageChangedToast(true)
    }
  }

  interface ParticipantProps {
    user: User
    manual: boolean
    currentUserData: Me | undefined
  }

  const handleLinkParticipantClicked = (user: User, serviceId: IntegrationServiceId) => {
    setServiceId(serviceId)
    setUser(user)
    setShowLinkContactModal(true)
  }

  const LinkParticipantButton: React.FC<{ user: User; serviceId: IntegrationServiceId; disable: boolean }> = ({ user, serviceId, disable }) => {
    if (user.id.startsWith('speaker')) {
      return (
        <Tooltip
          className='paragraph-small'
          title={'Configure Participant Assignments by clicking the gear icon before creating or updating contact data in connected systems.'}
          placement='top'
          offset={[0, 10]}
        >
          <Button variant='header' className='p-1 btn-header' disabled={true}>
            <StyledIntegrationIcon serviceId={serviceId} />
          </Button>
        </Tooltip>
      )
    }
    const serviceDisplayName = getIntegrationDisplayName(serviceId)
    return (
      <div className='d-flex'>
        <Tooltip
          className='paragraph-small'
          title={`Link your contact to their profile in ${serviceDisplayName} to pull their client data into Meeting Prep.`}
          placement='top'
          offset={[0, 10]}
        >
          <Button variant='header' className='p-1 btn-header' onClick={() => handleLinkParticipantClicked(user, serviceId)} disabled={disable}>
            <StyledIntegrationIcon serviceId={serviceId} />
          </Button>
        </Tooltip>
      </div>
    )
  }

  const Participant: React.FC<ParticipantProps> = ({ user, manual, currentUserData }) => {
    const { id: userId, type } = user
    const userType = currentUserId === userId ? 'current' : (type ?? 'anonymous')
    const { hasQuota } = useZocksQuota()
    const hasMultiLanguageSupportQuota = hasQuota('ai.multiLanguageSupport.value')
    const { multiLanguageEnabled } = useMultiLanguageEnabled()

    const isAnonymous = userType === 'anonymous'
    const isExternal = userType === 'external'

    const handleRemoveParticipantClicked = () => {
      setUserToRemove(user)
      setShowRemoveParticipantModal(true)
    }

    const getUnlinkedIntegrations = (): ExternalUserServiceIntegration[] => {
      if (!currentUserData?.integrations) {
        return []
      }

      const currentUserIntegrations = currentUserData.integrations

      const linkedServiceIds = user.integrations ? new Set(user.integrations.map((link) => link.serviceId)) : new Set()

      return currentUserIntegrations
        .filter((integration) => !linkedServiceIds.has(integration.serviceId))
        .filter((integration) => CONTACT_LINKING_SUPPORTED_INTEGRATIONS.includes(integration.serviceId))
    }

    return (
      <div className='assignment-border me-auto'>
        <Container className='mx-0 participant-container'>
          <Row className='px-3 py-2 d-flex justify-content-between align-items-center'>
            <Col className='p-0'>
              <Container className='p-0'>
                <Row className='participant-name-container p-0 d-flex gap-2 mx-0'>
                  <Col className='p-0 d-flex align-items-center gap-2' xs='auto'>
                    <div className='assignment-person-icon'>
                      <i className='bi bi-person-fill' />
                    </div>
                    {type === 'external' ? (
                      <NavLink
                        className={`${buttonStyles.button} ${buttonStyles.linkButton} button-link-medium`}
                        to={`/library/manage-contacts/contacts/${userId}`}
                      >
                        <div className='paragraph-medium bold'>{getUserDisplayName(user)}</div>
                      </NavLink>
                    ) : (
                      <div className='paragraph-medium bold'>{getUserDisplayName(user)}</div>
                    )}
                  </Col>
                  <Col className='p-0 d-flex align-items-center gap-2' xs='auto'>
                    <UserTag className='zocks-py-0.125 px-2' userType={userType} />
                    {user.settings?.language && hasMultiLanguageSupportQuota && multiLanguageEnabled ? (
                      <ContactLanguageDisplay language={user.settings.language} shortLabel />
                    ) : null}
                    {(isAnonymous || isExternal) && getUnlinkedIntegrations().length > 0
                      ? getUnlinkedIntegrations().map(({ serviceId }) => (
                          <LinkParticipantButton key={serviceId} user={user} serviceId={serviceId} disable={interactionsLoading} />
                        ))
                      : null}
                  </Col>
                </Row>
              </Container>
            </Col>
            {manual ? (
              <Col xs='auto'>
                <ScopedBlock
                  scope={{
                    scope: 'allocation_context:create',
                    superScope: ['allocation_context:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button variant='header' onClick={handleRemoveParticipantClicked}>
                    <i className='bi bi-trash3 d-flex align-items-center' />
                  </Button>
                </ScopedBlock>
              </Col>
            ) : null}
          </Row>
        </Container>
      </div>
    )
  }

  return (
    <>
      {showAddParticipantModal && (
        <AddParticipantModal
          show={showAddParticipantModal}
          existingClients={contacts}
          onCancel={() => setShowAddParticipantModal(false)}
          onSuccess={handleParticipantAdded}
          polling={pollingState.isActive}
        />
      )}
      {user && showLinkContactModal && serviceId ? (
        <LinkContactModal
          show={showLinkContactModal}
          onCancel={handleCancelLinkContactModal}
          onSave={handleSaveLinkContactModal}
          serviceId={serviceId}
          user={user}
        />
      ) : null}
      {userToRemove && showRemoveParticipantModal ? (
        <RemoveParticipantModal
          show={showRemoveParticipantModal}
          user={userToRemove}
          onCancel={() => {
            setShowRemoveParticipantModal(false)
            setUserToRemove(null)
          }}
          onSuccess={handleParticipantRemoved}
          polling={pollingState.isActive}
        />
      ) : null}
      {showLanguageModal && roomSession ? (
        <ChangeMeetingLanguageModal
          onHide={() => setShowLanguageModal(false)}
          participants={roomSession.users}
          languageConfig={roomSessionLanguageConfig}
          calculatedLanguageConfig={roomSession.config?.calculatedLanguage}
          oldLanguageConfig={oldLanguageConfig}
          onUpdateLanguage={handleUpdateLanguage}
          isUpdatingSessionConfig={isUpdatingSessionConfig}
          title={'Meeting language changed'}
          changeReason={languageChangeReason}
          onAcknowledge={handleLanguageAcknowledge}
          confirmButtonText={'Acknowledge'}
        />
      ) : null}
      <Container className='d-flex flex-column mx-0 w-100 mw-100 px-3 py-4 gap-3'>
        <Row className='m-0 d-flex gap-4'>
          <Col className='d-flex gap-2 align-items-center p-0' xs='auto'>
            <h5>Participants</h5>
          </Col>
          <Col className='p-0' xs='auto'>
            <ScopedBlock
              scope={{
                scope: 'allocation_context:create',
                superScope: ['allocation_context:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Button variant='header' type='button' onClick={handleAddParticipantClicked} disabled={roomSessionIsLoading}>
                <i className='bi bi-plus-circle' />
                Add Client
              </Button>
            </ScopedBlock>
          </Col>
        </Row>
        {contacts.length > 0 && (
          <Row className='m-0'>
            <Col className='p-0 gap-3'>
              {isUsersFetching ? (
                <div className='d-flex align-items-center justify-content-start'>
                  <Spinner />
                </div>
              ) : (
                <>
                  <Row className='m-0'>Clients ({contacts.length})</Row>
                  <div className='d-flex flex-column gap-1 participants-container'>
                    {contacts.map(({ user, manual }) => {
                      return <Participant key={user.id} user={user} manual={manual} currentUserData={currentUserData} />
                    })}
                  </div>
                </>
              )}
            </Col>
          </Row>
        )}
      </Container>
      {showLanguageChangedToast ? (
        <SummaryToast variant='success' show={showLanguageChangedToast} onClose={() => setShowLanguageChangedToast(false)} toastProps={{ delay: 3000 }}>
          <i className='bi bi-check-circle-fill' />
          <span className='paragraph-medium'>{toastText}</span>
        </SummaryToast>
      ) : null}
    </>
  )
}
